import { DeleteOutlined } from '@ant-design/icons';
import { Button, Row } from 'antd';
import EditableNorm from 'app/components/EditableNorm/EditableNorm';
import { StyledTable } from 'app/components/StyledTable/StyledTable';
import AddTaskResourceForm from 'app/containers/Task/parts/AddTaskResourceForm/AddTaskResourceForm';
import { findResourceById } from 'app/containers/Task/slice/helpers';
import { ITaskAvailableResource } from 'app/containers/Task/slice/types';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useGetTaskResourceOptions from 'utils/hooks/useGetTaskResourceOptions';
import { IRecipeResource } from '../../types';

interface IProps {
  isEditable?: boolean;
  selectedResources?: IRecipeResource[];
  setSelectedResources?: React.Dispatch<
    React.SetStateAction<IRecipeResource[]>
  >;
}

const RecipeResourceTable: FC<IProps> = ({
  isEditable = false,
  selectedResources,
  setSelectedResources = () => {},
}) => {
  const { t } = useTranslation();

  const { allResources } = useGetTaskResourceOptions();

  const recipeResources: IRecipeResource[] =
    selectedResources?.map(resource => ({
      ...resource,
      globalResource: findResourceById({
        allResources,
        resourceId: resource.id,
      }),
    })) || [];

  const addResource = (payload: IRecipeResource) => {
    setSelectedResources(prevState => [...prevState, payload]);
  };

  const removeResource = (resourceId: number) => {
    setSelectedResources(prevState =>
      prevState.filter(resource => resource.id !== resourceId),
    );
  };

  const updateResourceNorm = (resourceId: number, norm: number) => {
    setSelectedResources(prevState => {
      const resourceIdx = prevState.findIndex(res => res.id === resourceId);
      if (resourceIdx !== -1) {
        prevState[resourceIdx].norm = norm;
      }
      return prevState;
    });
  };

  const [activeNormInputResourceId, setActiveNormInputResourceId] = useState<
    number | null
  >(null);

  const columns = [
    {
      title: t('Task.ResourcesCard.Name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      render: (_: unknown, resource: IRecipeResource) => {
        const { globalResource } = resource;

        if (globalResource) {
          if (globalResource?.cultureVariation) {
            const cultureName = globalResource.cultureVariation.culture.name;
            const variationName = globalResource.cultureVariation.name;
            return `${cultureName} - ${variationName}`;
          }

          return `${globalResource.type.name} - ${globalResource.name}`;
        }
        return resource.name;
      },
      sorter: {
        compare: (a: IRecipeResource, b: IRecipeResource) => {
          return a.name.localeCompare(b.name);
        },
      },
    },

    {
      title: t('Task.ResourcesCard.RecommendedNorm', 'Rec. Norm'),
      dataIndex: 'recommendedNorm',
      key: 'recommendedNorm',
      render: (_: unknown, resource: IRecipeResource) =>
        `${resource.recommendedNorm} ${resource.globalResource?.type.unit}/Ha`,
      width: '15%',
      sorter: {
        compare: (a: IRecipeResource, b: IRecipeResource) => {
          return a.recommendedNorm - b.recommendedNorm;
        },
      },
    },
    {
      title: t('Task.ResourcesCard.Norm', 'Norm'),
      dataIndex: 'norm',
      key: 'norm',
      render: (norm: number, resource: IRecipeResource) => {
        return (
          <EditableNorm
            activeNormInputResourceId={activeNormInputResourceId}
            norm={norm}
            resourceId={resource.id}
            unit={resource.globalResource?.type.unit}
            onChange={(updatedNorm: number) => {
              updateResourceNorm(resource.id, updatedNorm);
            }}
            onOpen={() => setActiveNormInputResourceId(resource.id)}
            onClose={() => setActiveNormInputResourceId(null)}
            isEditable={isEditable}
          />
        );
      },
      width: '20%',
      sorter: {
        compare: (a: IRecipeResource, b: IRecipeResource) => {
          return a.norm - b.norm;
        },
      },
    },
    ...(isEditable
      ? [
          {
            title: '',
            dataIndex: 'deleteResource',
            key: 'deleteResource',
            render: (_: unknown, resource: IRecipeResource) => (
              <Button onClick={() => removeResource(resource.id)} type="text">
                <DeleteOutlined />
              </Button>
            ),
            width: '46px',
          },
        ]
      : []),
  ];

  const { options } = useGetTaskResourceOptions();

  const onFormSubmit = (resource: ITaskAvailableResource, norm: number) => {
    const payload: IRecipeResource = {
      globalResource: resource,
      id: resource.id,
      name: resource.name,
      norm,
      recommendedNorm: resource.recommendedNorm,
    };

    addResource(payload);
  };

  return (
    <Row>
      <div style={{ margin: '30px 0 15px' }}>
        {t('Recipe.Resources', {
          defaultValue: 'Resources: {{resourcesCount}}',
          resourcesCount: recipeResources?.length,
        })}
      </div>

      <StyledTable
        style={{ width: '100%' }}
        bordered={true}
        columns={columns}
        rowClassName="resource-row"
        dataSource={recipeResources}
        pagination={false}
      />
      {isEditable && (
        <AddTaskResourceForm
          onFormSubmit={onFormSubmit}
          availableResources={options}
        />
      )}
    </Row>
  );
};

export default RecipeResourceTable;
